import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useCurrentUser, useData } from '../../data/DataContext.tsx';
import type { FollowTarget } from '../../data/ports.ts';
import { Button } from '../../design/primitives.tsx';
import styles from './FollowButton.module.css';
import { useActivity } from '../activity/ActivityContext.tsx';

// Follow a person or a shop; onChange lets a page update its follower count.
// followsYou turns "Follow" into the design's "Follow back".
// look: 'button' (filled / outline), 'soft' (design: blue when not following,
// grey when following) or 'link' (blue text, for lists and the side rail).
export function FollowButton({ target, name, look = 'button', onChange, followsYou = false }: {
  target: FollowTarget; name: string; look?: 'button' | 'soft' | 'link'; onChange?: (following: boolean) => void; followsYou?: boolean;
}) {
  const { social } = useData();
  const { track } = useActivity();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const key = `${target.type}:${target.id}`;
  const [state, setState] = useState<{ key: string; following: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    social.isFollowing({ type: key.split(':')[0] as FollowTarget['type'], id: key.slice(key.indexOf(':') + 1) })
      .then(following => { if (active) setState({ key, following }); }, () => undefined);
    return () => { active = false; };
  }, [social, user, key]);

  const following = state?.key === key ? state.following : false;

  async function toggle() {
    if (!user) {
      navigate(`/auth/sign-in?next=${encodeURIComponent(location.pathname)}`);
      return;
    }
    setBusy(true);
    try {
      await social.setFollow(target, !following);
      if (!following) track({ type: 'follow', target: { kind: target.type === 'shop' ? 'shop' : 'person', id: target.id } });
      setState({ key, following: !following });
      onChange?.(!following);
    } finally {
      setBusy(false);
    }
  }

  if (look !== 'button') {
    return (
      <button type="button" className={[styles[look], following ? styles.on : ''].join(' ')} disabled={busy} aria-pressed={following} onClick={() => void toggle()} aria-label={`${following ? 'Unfollow' : 'Follow'} ${name}`}>
        {following ? 'Following' : followsYou ? 'Follow back' : 'Follow'}
      </button>
    );
  }
  return (
    <Button variant={following ? 'secondary' : 'primary'} disabled={busy} aria-pressed={following} onClick={() => void toggle()} aria-label={`${following ? 'Unfollow' : 'Follow'} ${name}`}>
      {following ? 'Following' : followsYou ? 'Follow back' : 'Follow'}
    </Button>
  );
}
